/**
 * Animation Configuration
 *
 * Standardized animation tokens for consistent micro-interactions.
 * All animations respect prefers-reduced-motion via CSS media queries.
 *
 * Design Principles:
 * - Subtle animations (200-300ms) for UI feedback
 * - Ease-out for enter animations (feels responsive)
 * - Ease-in for exit animations (feels natural)
 * - No animation if prefers-reduced-motion is set
 */

import { type SxProps, type Theme, keyframes } from '@mui/material/styles';

/**
 * Animation duration tokens (in milliseconds)
 */
export const DURATION = {
  instant: 100,
  fast: 150,
  normal: 200,
  slow: 300,
  slower: 400,
} as const;

/**
 * Easing functions
 */
export const EASING = {
  // Standard easing for most animations
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  // For elements entering the screen
  easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
  // For elements leaving the screen
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  // For sharp movements
  sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  // For bouncy effects
  bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
} as const;

/**
 * CSS transition strings
 */
export const transitions = {
  // All properties
  all: `all ${DURATION.normal}ms ${EASING.standard}`,
  allFast: `all ${DURATION.fast}ms ${EASING.standard}`,
  allSlow: `all ${DURATION.slow}ms ${EASING.standard}`,

  // Specific properties
  opacity: `opacity ${DURATION.normal}ms ${EASING.standard}`,
  transform: `transform ${DURATION.normal}ms ${EASING.easeOut}`,
  background: `background-color ${DURATION.fast}ms ${EASING.standard}`,
  color: `color ${DURATION.fast}ms ${EASING.standard}`,
  border: `border-color ${DURATION.fast}ms ${EASING.standard}`,
  shadow: `box-shadow ${DURATION.normal}ms ${EASING.standard}`,

  // Combined for interactive elements
  button: `background-color ${DURATION.fast}ms ${EASING.standard}, transform ${DURATION.fast}ms ${EASING.easeOut}, box-shadow ${DURATION.fast}ms ${EASING.standard}`,
  input: `border-color ${DURATION.fast}ms ${EASING.standard}, box-shadow ${DURATION.fast}ms ${EASING.standard}`,
} as const;

/**
 * Keyframe animations
 */
export const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

export const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const slideInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-16px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(16px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

export const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-4px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(4px);
  }
`;

/**
 * Animation style presets
 * Use with MUI sx prop
 *
 * All presets include animationFillMode: 'backwards' to prevent
 * flash of unstyled content before animation starts.
 */
export const animationStyles = {
  /**
   * Fade in animation
   */
  fadeIn: {
    animation: `${fadeIn} ${DURATION.normal}ms ${EASING.easeOut}`,
    animationFillMode: 'backwards',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  } as SxProps<Theme>,

  /**
   * Slide in from bottom
   */
  slideInUp: {
    animation: `${slideInUp} ${DURATION.slow}ms ${EASING.easeOut}`,
    animationFillMode: 'backwards',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  } as SxProps<Theme>,

  /**
   * Slide in from top
   */
  slideInDown: {
    animation: `${slideInDown} ${DURATION.slow}ms ${EASING.easeOut}`,
    animationFillMode: 'backwards',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  } as SxProps<Theme>,

  /**
   * Scale in animation (good for modals)
   */
  scaleIn: {
    animation: `${scaleIn} ${DURATION.normal}ms ${EASING.easeOut}`,
    animationFillMode: 'backwards',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  } as SxProps<Theme>,

  /**
   * Shimmer effect for skeletons
   */
  shimmer: {
    background: `linear-gradient(
      90deg,
      rgba(232, 232, 240, 0.03) 25%,
      rgba(232, 232, 240, 0.06) 50%,
      rgba(232, 232, 240, 0.03) 75%
    )`,
    backgroundSize: '200% 100%',
    animation: `${shimmer} 1.5s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      background: 'rgba(232, 232, 240, 0.04)',
    },
  } as SxProps<Theme>,

  /**
   * Pulse animation (for loading indicators)
   */
  pulse: {
    animation: `${pulse} 1.5s ease-in-out infinite`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  } as SxProps<Theme>,

  /**
   * Spin animation (for spinners)
   */
  spin: {
    animation: `${spin} 1s linear infinite`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  } as SxProps<Theme>,

  /**
   * Shake animation (for form errors)
   */
  shake: {
    animation: `${shake} 0.5s ease-in-out`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  } as SxProps<Theme>,
};

/**
 * Interactive element hover/active styles
 */
export const interactionStyles = {
  /**
   * Button lift effect on hover
   */
  buttonLift: {
    transition: transitions.button,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 2,
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&:hover': {
        transform: 'none',
      },
      '&:active': {
        transform: 'none',
      },
    },
  } as SxProps<Theme>,

  /**
   * Scale effect on hover (for cards, images)
   */
  scaleOnHover: {
    transition: transitions.transform,
    '&:hover': {
      transform: 'scale(1.02)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&:hover': {
        transform: 'none',
      },
    },
  } as SxProps<Theme>,

  /**
   * Subtle highlight on hover (for list items)
   */
  highlightOnHover: {
    transition: transitions.background,
    '&:hover': {
      backgroundColor: 'action.hover',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  } as SxProps<Theme>,

  /**
   * Press effect (scale down slightly)
   */
  pressEffect: {
    transition: transitions.transform,
    '&:active': {
      transform: 'scale(0.98)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '&:active': {
        transform: 'none',
      },
    },
  } as SxProps<Theme>,
};

/**
 * Page transition styles
 */
export const pageTransitionStyles = {
  /**
   * Page enter animation
   */
  enter: {
    animation: `${fadeIn} ${DURATION.normal}ms ${EASING.easeOut}, ${slideInUp} ${DURATION.slow}ms ${EASING.easeOut}`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  } as SxProps<Theme>,

  /**
   * Simple fade for page transitions
   */
  fade: {
    animation: `${fadeIn} ${DURATION.normal}ms ${EASING.easeOut}`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  } as SxProps<Theme>,
};

/**
 * Staggered animation delay generator
 * Use for list items appearing one by one
 */
export function staggerDelay(index: number, baseDelay = 50): SxProps<Theme> {
  return {
    animationDelay: `${index * baseDelay}ms`,
    animationFillMode: 'backwards',
  };
}

/**
 * Creates animation styles with custom duration
 */
export function createAnimation(
  animation: ReturnType<typeof keyframes>,
  duration: number = DURATION.normal,
  easing: string = EASING.easeOut
): SxProps<Theme> {
  return {
    animation: `${animation} ${duration}ms ${easing}`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  };
}
